// FlashCard.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Flashcards.module.css';
import BackButton from '../../common/BackButton/BackButton';
import { apiRequest } from '../../../config/api';

const Flashcards = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [animateWord, setAnimateWord] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [words, setWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [wordMasteredMessage, setWordMasteredMessage] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get the current word
  const currentWord = words[currentWordIndex] || {};
  
  // Set defaults if no word is loaded yet
  const displayWord = currentWord.word || "Loading...";
  const displayPos = currentWord.partOfSpeech || "noun";
  const displayDefinition = currentWord.definition || "Loading definition...";
  const displayExample = currentWord.examples?.[0]?.sentence || "No example available.";
  
  // Add animation to word after component mounts
  useEffect(() => {
    const element = document.querySelector('.bg-main');
    if (element) {
      element.style.background = 'linear-gradient(135deg, #6a1b9a, #9c27b0)';
    }

    const timer = setTimeout(() => {
      setAnimateWord(true);
    }, 500);
    
    return () => {
      if (element) {
        element.style.background = '#f5f5f5';
      }
      clearTimeout(timer);
    };
  }, [currentWordIndex]);
  
  const fetchFlashcards = async (mode) => {
    if (mode === 'recall') {
      // Navigate to GuessTheMeaning component for recall mode
      navigate('/vocabulary/guess-meaning', { 
        state: { 
          mode: 'learn_practice',
          subMode: 'recall',
          difficulty: location.state?.difficulty || 4,
          listId: location.state?.listId,
          theme: location.state?.theme || 'general'
        } 
      });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const { state } = location;
      const difficulty = state?.difficulty || 4;
      const listId = state?.listId;
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        mode: 'learn',
        difficulty,
        count: 10,
        theme: state?.theme || 'general'
      });
      
      if (listId) {
        queryParams.append('listId', listId);
      }
      
      const response = await apiRequest(`/vocabulary/flashcards?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      setWords(response.words);
      setSessionId(response.sessionId);
      setLoading(false);
      setIsFirstVisit(false);
      setCurrentWordIndex(0);
    } catch (err) {
      setError('Failed to load flashcards. Please try again.');
      setLoading(false);
      console.error('Error fetching flashcards:', err);
    }
  };
  
  const handleClick = () => {
    if (!isFlipped) {
      // Record the time when the card is flipped to see the answer
      setStartTime(Date.now());
    }
    setIsFlipped(!isFlipped);
  };
  
  const handleMouseEnter = () => {
    setIsHovering(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const moveToNextWord = () => {
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(currentWordIndex + 1);
      setIsFlipped(false);
    } else {
      // End of flashcards
      navigate('/', { 
        state: { 
          message: 'Flashcard session completed successfully!' 
        } 
      });
    }
  };
  
  const handleKnewWord = async () => {
    if (!sessionId || !currentWord.id) return;
    
    try {
      const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
      const response = await apiRequest('/vocabulary/flashcards/response', {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          wordId: currentWord.id,
          isCorrect: true,
          timeSpent
        })
      });
      
      // Check if the word was mastered and removed from review list
      if (response.wordMastered) {
        setWordMasteredMessage(response.message || "Word mastered! Removed from review list.");
        
        // Clear the message after 3 seconds
        setTimeout(() => {
          setWordMasteredMessage(null);
        }, 3000);
      }
      
      moveToNextWord();
    } catch (error) {
      console.error('Error updating response:', error);
    }
  };
  
  const handleAudioClick = (e) => {
    e.stopPropagation(); // Prevent card from flipping
    
    if (currentWord.pronunciation?.audioUrl) {
      const audio = new Audio(currentWord.pronunciation.audioUrl);
      audio.play().catch(err => console.error('Error playing audio:', err));
    }
  };
  
  // Highlight the word in the example sentence
  const highlightedExample = displayExample.replace(
    new RegExp(`\\b${displayWord}\\b`, 'gi'),
    `<span class="${styles.highlightedWord}">$&</span>`
  );
  
  // If it's the first visit, show the mode selection
  if (isFirstVisit && !loading) {
    return (
      <div className={styles.container}>
        <div className={styles.modeSelection}>
          <h2 className={styles.optionTitle}>Choose One :</h2>
          <div className={styles.buttonContainer}>
            <button 
              className={styles.optionButton}
              onClick={() => fetchFlashcards('learn')}
            >
              Learn New Words
            </button>
            <button 
              className={styles.optionButton}
              onClick={() => fetchFlashcards('recall')}
            >
              Recall Words
            </button>
          </div>
        </div>
        <BackButton/>
      </div>
    );
  }
  
  return (
    <div className={styles.container}>
      {wordMasteredMessage && (
        <div className={styles.masteredMessage}>
          {wordMasteredMessage}
        </div>
      )}
      
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : (
        <div 
          className={`${styles.card} ${isHovering ? styles.hover : ''}`}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className={`${styles.cardInner} ${isFlipped ? styles.flipped : ''}`}>
            {/* Front of the card */}
            <div className={styles.cardFront}>
              <div className={styles.cardContent}>
                <div className={styles.wordContainer}>
                  <h1 className={`${styles.word} ${animateWord ? styles.wordAnimate : ''}`}>
                    {displayWord}
                  </h1>
                </div>
                
                <div className={styles.tapInstruction}>
                  Tap to see meaning
                </div>
              </div>
            </div>
            
            {/* Back of the card */}
            <div className={styles.cardBack}>
              <div className={styles.cardContent}>
                <div className={styles.wordWithAudio}>
                  <h1 className={styles.word}>{displayWord}</h1>
                  <svg 
                    className={styles.audioIcon} 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={handleAudioClick}
                  >
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                
                <div className={styles.definition}>
                  <span className={styles.partOfSpeech}>{displayPos}:</span>
                  <span className={styles.definitionText}>{displayDefinition}</span>
                </div>
                
                <div 
                  className={styles.example}
                  dangerouslySetInnerHTML={{ __html: highlightedExample }}
                />
                
                <div className={styles.feedbackContainer}>
                  <div 
                    className={styles.knewWord}
                    onClick={handleKnewWord}
                  >
                    <span className={styles.feedbackText}>
                      Next
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <BackButton/>
    </div>
  );
};

export default Flashcards;